import { ButtonContent } from '../../ui/ButtonContent';
import React, { useState, useRef } from 'react';
import { useSupabase } from '../../../lib/SupabaseProvider';
import { captureException } from '../../../lib/telemetry/posthog/frontend';
import { Avatar } from '../Avatar';
import { AnimatedLabel } from '../../ui/AnimatedLabel';
import { AnimatedPresence } from '../../ui/AnimatedPresence';

/**
 * Profile section of the Settings page.
 * Handles avatar upload (canvas center-crop), name editing, and metadata persistence.
 */
export const ProfileSection: React.FC = () => {
  const { user, supabase } = useSupabase();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [firstName, setFirstName] = useState(user?.user_metadata?.first_name || '');
  const [lastName, setLastName] = useState(user?.user_metadata?.last_name || '');
  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>(
    user?.user_metadata?.profile_image_url || undefined
  );
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  /** Center-crop and resize image to 320x320 PNG blob via canvas. */
  const processImage = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const size = Math.min(img.width, img.height);
        const sx = (img.width - size) / 2;
        const sy = (img.height - size) / 2;

        const canvas = document.createElement('canvas');
        canvas.width = 320;
        canvas.height = 320;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas context unavailable')); return; }

        ctx.drawImage(img, sx, sy, size, size, 0, 0, 320, 320);
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
          'image/png',
          0.85
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  /** Handle file selection for avatar upload. */
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    // Reset file input so the same file can be re-selected
    e.target.value = '';

    setUploading(true);
    setMessage(null);

    try {
      const blob = await processImage(file);
      const filePath = `${user.id}/profile.png`;

      const { error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(filePath, blob, {
          contentType: 'image/png',
          upsert: true,
          cacheControl: '0',
        });

      if (uploadError) throw uploadError;

      // Get public URL and append cache-bust
      const { data: { publicUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(filePath);

      const urlWithCacheBust = `${publicUrl}?t=${Date.now()}`;

      // Update user metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: { profile_image_url: urlWithCacheBust },
      });

      if (updateError) throw updateError;

      setProfileImageUrl(urlWithCacheBust);
      setMessage({ type: 'success', text: 'Profile photo updated.' });
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        'ProfileSection.handleAvatarUpload',
        { userId: user.id }
      );
      setMessage({ type: 'error', text: 'Failed to upload photo. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  /** Remove avatar from storage and clear metadata. */
  const handleRemoveAvatar = async () => {
    if (!user?.id) return;

    setRemoving(true);
    setMessage(null);

    try {
      await supabase.storage
        .from('profile-images')
        .remove([`${user.id}/profile.png`]);

      const { error: updateError } = await supabase.auth.updateUser({
        data: { profile_image_url: null },
      });

      if (updateError) throw updateError;

      setProfileImageUrl(undefined);
      setMessage({ type: 'success', text: 'Profile photo removed.' });
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        'ProfileSection.handleRemoveAvatar',
        { userId: user.id }
      );
      setMessage({ type: 'error', text: 'Failed to remove photo. Please try again.' });
    } finally {
      setRemoving(false);
    }
  };

  /** Save name changes to user metadata. */
  const handleSave = async () => {
    if (!user?.id) return;

    setSaving(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        },
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Profile updated successfully.' });
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        'ProfileSection.handleSave',
        { userId: user.id }
      );
      setMessage({ type: 'error', text: 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    firstName.trim() !== (user?.user_metadata?.first_name || '') ||
    lastName.trim() !== (user?.user_metadata?.last_name || '');

  return (
    <section>
      <h2 className="text-lg font-semibold text-foreground mb-6">Profile</h2>

      {/* Avatar */}
      <div className="flex items-center gap-6 mb-8">
        <Avatar
          firstName={firstName || user?.email?.split('@')[0] || 'U'}
          lastName={lastName}
          profileImageUrl={profileImageUrl}
          size="large"
        />
        <div className="space-y-2">
          <div className="flex gap-2">
            <button aria-busy={uploading}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || removing}
              className="px-3 py-1.5 bg-surface text-foreground text-sm rounded-lg hover:bg-surface-hover transition-[background-color,color,opacity] duration-200 motion-reduce:transition-none disabled:opacity-50"
            >
              <ButtonContent loading={uploading}>Upload photo</ButtonContent>
            </button>
            {profileImageUrl && (
              <button aria-busy={removing}
                onClick={handleRemoveAvatar}
                disabled={uploading || removing}
                className="px-3 py-1.5 text-muted-foreground text-sm rounded-lg hover:text-danger hover:bg-surface-hover transition-[background-color,color,opacity] duration-200 motion-reduce:transition-none disabled:opacity-50"
              >
                <ButtonContent loading={removing}>Remove</ButtonContent>
              </button>
            )}
          </div>
          <p className="text-xs text-subtle-foreground">JPG or PNG. Max 5MB.</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={handleAvatarUpload}
        />
      </div>

      {/* Name fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium block mb-2">
            First Name
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm placeholder-subtle-foreground focus:outline-none focus:border-volt"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium block mb-2">
            Last Name
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm placeholder-subtle-foreground focus:outline-none focus:border-volt"
          />
        </div>
      </div>

      {/* Status message */}
      <AnimatedPresence show={!!message}>
        <div className={`text-sm pb-4 transition-colors ${message?.type === 'success' ? 'text-success' : 'text-danger'}`}>
          <AnimatedLabel text={message?.text ?? ''} multiline />
        </div>
      </AnimatedPresence>

      {/* Save */}
      <button aria-busy={saving}
        onClick={handleSave}
        disabled={saving || !hasChanges}
        className="px-6 py-2 button-accent font-bold text-sm rounded-lg transition-[background-color,color,opacity] duration-200 motion-reduce:transition-none disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ButtonContent loading={saving}>Save Changes</ButtonContent>
      </button>
    </section>
  );
};
